import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Progress,
  Tooltip,
  Modal,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Badge,
  Drawer,
  Descriptions,
  Tabs,
  Form,
  Spin,
} from "antd";
import {
  SearchOutlined,
  DeleteOutlined,
  EyeOutlined,
  EditOutlined,
  SaveOutlined,
  PlusOutlined,
  ReloadOutlined,
  GlobalOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DashboardOutlined,
  RocketOutlined,
  BarChartOutlined,
  ApiOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import {
  getProgrammaticSeoPages,
  getProgrammaticSeoPageById,
  updateProgrammaticSeoPage,
  toggleProgrammaticSeoPageStatus,
  deleteProgrammaticSeoPage,
  createProgrammaticSeoCampaign,
} from "@api/seo/programmaticSeo";
import "./ProgrammaticSeoPages.css";

const { Search } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

const ProgrammaticSeoPages = () => {
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [pages, setPages] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [filters, setFilters] = useState({
    pageType: null,
    status: null,
    qualityScore: null,
    search: "",
  });
  const [selectedPage, setSelectedPage] = useState(null);
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [generateForm] = Form.useForm();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [editingPage, setEditingPage] = useState(null);
  const [pageDetails, setPageDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [searchText, setSearchText] = useState("");
  const pageJumpInputRef = useRef(null);

  useEffect(() => {
    // Get URL parameters on component mount
    const searchParams = new URLSearchParams(window.location.search);
    const page = parseInt(searchParams.get("page") || "1", 10);
    const pageSize = parseInt(searchParams.get("pageSize") || "10", 10);
    const search = searchParams.get("search") || "";
    const pageType = searchParams.get("pageType") || null;
    const status = searchParams.get("status") || null;
    const qualityScore = searchParams.get("qualityScore") || null;

    // Set initial state from URL
    setSearchText(search);
    setPagination({ current: page, pageSize });
    setFilters({
      pageType,
      status,
      qualityScore,
      search,
    });

    // Fetch pages with URL parameters
    fetchPages(page, pageSize, { pageType, status, qualityScore, search });
  }, []);

  const fetchPages = async (
    page = pagination.current,
    pageSize = pagination.pageSize,
    customFilters = filters
  ) => {
    setLoading(true);
    try {
      const response = await getProgrammaticSeoPages({
        page,
        perPage: pageSize,
        status: customFilters.status,
        pageType: customFilters.pageType,
        searchText: customFilters.search,
        sortBy: "-createdAt",
      });

      const pagesData = response.data?.data || response.data;
      setPages(pagesData.pages || []);
      setTotal(pagesData.total || 0);
    } catch (error) {
      message.error("Failed to fetch pages");
      console.error("Error fetching pages:", error);
      setPages([]);
      setTotal(0);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const updateURL = (newPagination, newFilters) => {
    const searchParams = new URLSearchParams();
    searchParams.set("page", newPagination.current);
    searchParams.set("pageSize", newPagination.pageSize);
    if (newFilters.search) searchParams.set("search", newFilters.search);
    if (newFilters.pageType) searchParams.set("pageType", newFilters.pageType);
    if (newFilters.status) searchParams.set("status", newFilters.status);
    if (newFilters.qualityScore)
      searchParams.set("qualityScore", newFilters.qualityScore);

    history.push({ search: searchParams.toString() });
  };

  const handleDelete = async (pageId) => {
    try {
      await deleteProgrammaticSeoPage(pageId);
      message.success("Page deleted successfully");
      fetchPages(pagination.current, pagination.pageSize, filters);
    } catch (error) {
      message.error("Failed to delete page");
    }
  };

  const handleStatusToggle = async (pageId, currentStatus) => {
    try {
      // Toggle logic: any non-active status (draft, disabled, paused) becomes active
      // and active becomes disabled
      const newStatus = currentStatus === "active" ? "disabled" : "active";
      await toggleProgrammaticSeoPageStatus(pageId, newStatus);
      message.success(
        `Page ${newStatus === "active" ? "enabled" : "disabled"} successfully`
      );
      fetchPages(pagination.current, pagination.pageSize, filters);
    } catch (error) {
      message.error("Failed to update page status");
    }
  };

  const fetchPageDetails = async (pageId) => {
    setLoadingDetails(true);
    try {
      const response = await getProgrammaticSeoPageById(pageId);
      const pageData = response.data?.data || response.data;
      setPageDetails(pageData);
      return pageData;
    } catch (error) {
      message.error("Failed to fetch page details");
      return null;
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleEdit = async (page) => {
    const details = await fetchPageDetails(page._id);
    if (details) {
      setEditingPage(details);
      editForm.setFieldsValue({
        title: details.title,
        metaDescription: details.metaDescription,
        h1: details.h1,
        content: JSON.stringify(details.content, null, 2),
        status: details.status,
      });
      setEditModalVisible(true);
    }
  };

  const handleUpdate = async (values) => {
    try {
      setLoading(true);
      const updateData = {
        ...values,
        content: JSON.parse(values.content),
      };

      await updateProgrammaticSeoPage(editingPage._id, updateData);
      message.success("Page updated successfully");
      setEditModalVisible(false);
      editForm.resetFields();
      fetchPages(pagination.current, pagination.pageSize, filters);
    } catch (error) {
      if (error.message.includes("JSON")) {
        message.error("Invalid JSON format in content");
      } else {
        message.error("Failed to update page");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePage = async (values) => {
    try {
      setLoading(true);

      // Create a mini campaign to generate a single page
      const campaignData = {
        name: `Single Page - ${values.pageType} - ${new Date().toISOString()}`,
        description: "Single page generation",
        pageType: values.pageType,
        totalPages: 1,
        pagesPerBatch: 1,
        scheduledStart: new Date(),
        scheduledEnd: new Date(Date.now() + 24 * 60 * 60 * 1000), // 1 day
        timeDistribution: "evenly",
        minQualityScore: 70,
        aiProvider: {
          preferredProvider: "template",
          enableAI: false,
        },
        filters: values.filters || {},
      };

      await createProgrammaticSeoCampaign(campaignData);
      message.success("Page generation started. Check campaigns for progress.");
      setGenerateModalVisible(false);
      generateForm.resetFields();

      // Refresh pages after a delay
      setTimeout(
        () => fetchPages(pagination.current, pagination.pageSize, filters),
        3000
      );
    } catch (error) {
      message.error("Failed to generate page");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <a
            href={`${process.env.REACT_APP_LANDING_PAGE_URL}${record.url}`}
            target="_blank"
            rel="noopener noreferrer"
            className="page-title-link"
          >
            {text}
          </a>
          <span className="page-url">{record.url}</span>
        </Space>
      ),
      ellipsis: true,
    },
    {
      title: "Type",
      dataIndex: "pageType",
      key: "pageType",
      width: 120,
      filters: [
        { text: "AI Copilot", value: "copilotType" },
        { text: "Industry Solutions", value: "industry" },
        { text: "Professional Tasks", value: "professionalTask" },
        { text: "Location Services", value: "location" },
        { text: "Location + Copilot", value: "locationCopilot" },
        { text: "Industry + Copilot", value: "industryCopilot" },
        { text: "Task + Copilot", value: "taskCopilot" },
        { text: "Comparison", value: "comparison" },
      ],
      render: (type) => {
        const colors = {
          copilotType: "blue",
          industry: "green",
          professionalTask: "purple",
          location: "orange",
          locationCopilot: "geekblue",
          industryCopilot: "cyan",
          taskCopilot: "magenta",
          comparison: "red",
        };
        const typeText = {
          copilotType: "AI Copilot",
          industry: "Industry Solutions",
          professionalTask: "Professional Tasks",
          location: "Location Services",
          locationCopilot: "Location + Copilot",
          industryCopilot: "Industry + Copilot",
          taskCopilot: "Task + Copilot",
          comparison: "Comparison",
        };
        return <Tag color={colors[type]}>{typeText[type] || type}</Tag>;
      },
    },
    {
      title: "Quality Score",
      dataIndex: "qualityScore",
      key: "qualityScore",
      width: 140,
      sorter: true,
      render: (score) => (
        <Progress
          percent={score}
          size="small"
          strokeColor={
            score >= 80 ? "#52c41a" : score >= 60 ? "#faad14" : "#f5222d"
          }
        />
      ),
    },
    {
      title: "Traffic",
      dataIndex: ["performanceMetrics", "organicTraffic"],
      key: "traffic",
      width: 100,
      sorter: true,
      render: (traffic) => traffic?.toLocaleString() || "0",
    },
    {
      title: "Indexed",
      dataIndex: ["performanceMetrics", "indexed"],
      key: "indexed",
      width: 90,
      render: (indexed) =>
        indexed ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Yes
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">
            No
          </Tag>
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      filters: [
        { text: "All", value: null },
        { text: "Enabled", value: "active" },
        { text: "Disabled", value: "disabled" },
        { text: "Draft", value: "draft" },
      ],
      render: (status) => {
        const statusConfig = {
          active: { color: "success", text: "Enabled" },
          disabled: { color: "warning", text: "Disabled" },
          paused: { color: "warning", text: "Disabled" }, // Legacy support
          draft: { color: "default", text: "Draft" },
          deleted: { color: "error", text: "Deleted" },
        };
        const config = statusConfig[status] || statusConfig.draft;
        return <Badge status={config.color} text={config.text} />;
      },
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      sorter: true,
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={async () => {
                const details = await fetchPageDetails(record._id);
                if (details) {
                  setSelectedPage(details);
                  setDetailDrawerVisible(true);
                }
              }}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title={record.status === "active" ? "Disable" : "Enable"}>
            <Button
              type="text"
              icon={
                record.status === "active" ? (
                  <PauseCircleOutlined />
                ) : (
                  <PlayCircleOutlined />
                )
              }
              onClick={() => handleStatusToggle(record._id, record.status)}
            />
          </Tooltip>
          <Tooltip title="View Live">
            <Button
              type="text"
              icon={<GlobalOutlined />}
              href={`${process.env.REACT_APP_LANDING_PAGE_URL}${record.url}`}
              target="_blank"
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this page?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleTableChange = (newPagination, tableFilters, sorter) => {
    const newFilters = {
      ...filters,
      pageType: tableFilters.pageType?.[0] || null,
      status: tableFilters.status?.[0] || null,
    };

    setPagination(newPagination);
    setFilters(newFilters);
    updateURL(newPagination, newFilters);
    fetchPages(newPagination.current, newPagination.pageSize, newFilters);
  };

  const handleSearch = (value) => {
    const newFilters = { ...filters, search: value };
    const newPagination = { ...pagination, current: 1 };

    setSearchText(value);
    setFilters(newFilters);
    setPagination(newPagination);
    updateURL(newPagination, newFilters);
    fetchPages(1, pagination.pageSize, newFilters);
  };

  const handleQualityScoreChange = (value) => {
    const newFilters = { ...filters, qualityScore: value };
    const newPagination = { ...pagination, current: 1 };

    setFilters(newFilters);
    setPagination(newPagination);
    updateURL(newPagination, newFilters);
    fetchPages(1, pagination.pageSize, newFilters);
  };

  const handlePageTypeChange = (value) => {
    const newFilters = { ...filters, pageType: value };
    const newPagination = { ...pagination, current: 1 };

    setFilters(newFilters);
    setPagination(newPagination);
    updateURL(newPagination, newFilters);
    fetchPages(1, pagination.pageSize, newFilters);
  };

  const handlePageJump = (pageNumber) => {
    const maxPage = Math.ceil(total / pagination.pageSize);
    if (isNaN(pageNumber) || pageNumber < 1 || pageNumber > maxPage) {
      message.warning(`Please enter a valid page number between 1 and ${maxPage}`);
      return;
    }
    
    if (pageNumber === pagination.current) {
      message.info(`You are already on page ${pageNumber}`);
      return;
    }

    const newPagination = { ...pagination, current: pageNumber };
    setPagination(newPagination);
    updateURL(newPagination, filters);
    fetchPages(pageNumber, pagination.pageSize, filters);
  };

  const GeneratePageForm = () => (
    <Form form={generateForm} layout="vertical" onFinish={handleGeneratePage}>
      <Form.Item
        name="pageType"
        label="Page Type"
        rules={[{ required: true, message: "Please select page type" }]}
      >
        <Select placeholder="Select page type">
          <Option value="copilotType">AI Copilot</Option>
          <Option value="industry">Industry Solutions</Option>
          <Option value="professionalTask">Professional Tasks</Option>
          <Option value="location">Location Services</Option>
          <Option value="locationCopilot">Location + Copilot</Option>
          <Option value="industryCopilot">Industry + Copilot</Option>
          <Option value="taskCopilot">Task + Copilot</Option>
          <Option value="comparison">Comparison</Option>
        </Select>
      </Form.Item>

      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) =>
          prevValues.pageType !== currentValues.pageType
        }
      >
        {({ getFieldValue }) => {
          const pageType = getFieldValue("pageType");

          if (pageType === "comparison") {
            return (
              <>
                <Form.Item
                  name={["metadata", "competitor"]}
                  label="Competitor"
                  rules={[{ required: true }]}
                >
                  <Input.TextArea
                    placeholder='{"name": "Zapier", "slug": "zapier", "category": "automation"}'
                    rows={3}
                  />
                </Form.Item>
                <Form.Item
                  name={["metadata", "useCase"]}
                  label="Use Case"
                  rules={[{ required: true }]}
                >
                  <Input.TextArea
                    placeholder='{"name": "E-commerce Automation", "slug": "ecommerce", "industry": "retail"}'
                    rows={3}
                  />
                </Form.Item>
              </>
            );
          }

          if (pageType === "integration") {
            return (
              <Form.Item
                name={["metadata", "integration"]}
                label="Integration Tools"
                rules={[{ required: true }]}
              >
                <Input.TextArea
                  placeholder='{"tool1": {"name": "Slack", "slug": "slack"}, "tool2": {"name": "Gmail", "slug": "gmail"}}'
                  rows={4}
                />
              </Form.Item>
            );
          }

          if (pageType === "location") {
            return (
              <Form.Item
                name={["metadata", "location"]}
                label="Location"
                rules={[{ required: true }]}
              >
                <Input.TextArea
                  placeholder='{"cityName": "New York", "state": "NY", "country": "USA", "population": 8400000}'
                  rows={3}
                />
              </Form.Item>
            );
          }

          if (pageType === "useCase") {
            return (
              <Form.Item
                name={["metadata", "useCase"]}
                label="Use Case"
                rules={[{ required: true }]}
              >
                <Input.TextArea
                  placeholder='{"name": "Lead Generation", "slug": "lead-generation", "industry": "marketing"}'
                  rows={3}
                />
              </Form.Item>
            );
          }

          if (pageType === "locationUseCase") {
            return (
              <>
                <Form.Item
                  name={["metadata", "location"]}
                  label="Location"
                  rules={[{ required: true }]}
                >
                  <Input.TextArea
                    placeholder='{"cityName": "New York", "state": "NY", "country": "USA", "population": 8400000}'
                    rows={3}
                  />
                </Form.Item>
                <Form.Item
                  name={["metadata", "useCase"]}
                  label="Use Case"
                  rules={[{ required: true }]}
                >
                  <Input.TextArea
                    placeholder='{"name": "Lead Generation", "slug": "lead-generation", "industry": "marketing"}'
                    rows={3}
                  />
                </Form.Item>
              </>
            );
          }

          if (pageType === "integrationUseCase") {
            return (
              <>
                <Form.Item
                  name={["metadata", "integration"]}
                  label="Integration"
                  rules={[{ required: true }]}
                >
                  <Input.TextArea
                    placeholder='{"name": "Slack", "slug": "slack", "category": "communication"}'
                    rows={3}
                  />
                </Form.Item>
                <Form.Item
                  name={["metadata", "useCase"]}
                  label="Use Case"
                  rules={[{ required: true }]}
                >
                  <Input.TextArea
                    placeholder='{"name": "Lead Generation", "slug": "lead-generation", "industry": "marketing"}'
                    rows={3}
                  />
                </Form.Item>
              </>
            );
          }

          return null;
        }}
      </Form.Item>

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            Generate Page
          </Button>
          <Button onClick={() => setGenerateModalVisible(false)}>Cancel</Button>
        </Space>
      </Form.Item>
    </Form>
  );

  const navigateTo = (path) => {
    const basePath =
      user?.role === "superadmin" ? "/super-admin" : "/marketing-admin";
    history.push(`${basePath}${path}`);
  };

  return (
    <div className="programmatic-seo-pages">
      {/* Header Section */}
      <div className="seo-dashboard-header">
        <Row justify="space-between" align="middle">
          <Col>
            <h1>Programmatic SEO Pages</h1>
            <p>
              Manage and monitor all AI-generated SEO pages with performance
              tracking
            </p>
            <div className="seo-nav-pills">
              <Button
                icon={<DashboardOutlined />}
                onClick={() => navigateTo("/seo/programmatic-seo/dashboard")}
              >
                Dashboard
              </Button>
              <Button
                icon={<RocketOutlined />}
                onClick={() => navigateTo("/seo/programmatic-seo/campaigns")}
              >
                Campaigns
              </Button>
              <Button
                icon={<BarChartOutlined />}
                onClick={() => navigateTo("/seo/programmatic-seo/analytics")}
              >
                Analytics
              </Button>
              <Button type="primary" icon={<FileTextOutlined />}>
                Pages
              </Button>
            </div>
          </Col>
          <Col>
            <Space className="action-button-group">
              <Button
                className="seo-action-btn seo-action-btn-secondary"
                icon={<ApiOutlined />}
                onClick={() => navigateTo("/seo/ai-apps")}
              >
                AI Apps
              </Button>
              <Button
                className="seo-action-btn seo-action-btn-secondary"
                icon={<GlobalOutlined />}
                onClick={() => navigateTo("/seo/seo-glossary")}
              >
                SEO Glossary
              </Button>
              <Button
                className="seo-action-btn seo-action-btn-secondary"
                icon={<ReloadOutlined />}
                onClick={() =>
                  fetchPages(pagination.current, pagination.pageSize, filters)
                }
              >
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <div className="seo-dashboard-content">
        <Spin spinning={initialLoad} size="large" tip="Loading pages...">
            {/* Search and Filters */}
            <Card className="filter-section" style={{ marginBottom: 24 }}>
              <Row justify="space-between" align="middle" gutter={[16, 16]}>
                <Col xs={24} md={14}>
                  <Space wrap>
                    <Search
                      placeholder="Search pages..."
                      allowClear
                      enterButton={<SearchOutlined />}
                      value={searchText}
                      onChange={(e) => {
                        setSearchText(e.target.value);
                        // Clear search when input is emptied
                        if (!e.target.value && filters.search) {
                          handleSearch("");
                        }
                      }}
                      onSearch={handleSearch}
                      style={{ width: 250 }}
                    />
                    <Select
                      placeholder="Page Type"
                      allowClear
                      value={filters.pageType}
                      onChange={handlePageTypeChange}
                      style={{ width: 200 }}
                    >
                      <Option value="copilotType">🤖 AI Copilot</Option>
                      <Option value="industry">🏭 Industry Solutions</Option>
                      <Option value="professionalTask">💼 Professional Tasks</Option>
                      <Option value="location">📍 Location Services</Option>
                      <Option value="locationCopilot">📍 Location + Copilot</Option>
                      <Option value="industryCopilot">🏭 Industry + Copilot</Option>
                      <Option value="taskCopilot">💼 Task + Copilot</Option>
                      <Option value="comparison">📊 Comparison</Option>
                    </Select>
                    <Select
                      placeholder="Quality Score"
                      allowClear
                      value={filters.qualityScore}
                      onChange={handleQualityScoreChange}
                      style={{ width: 150 }}
                    >
                      <Option value={80}>≥ 80 (High)</Option>
                      <Option value={60}>≥ 60 (Medium)</Option>
                      <Option value={0}>All</Option>
                    </Select>
                  </Space>
                </Col>
                <Col xs={24} md={10}>
                  <Space wrap style={{ justifyContent: 'flex-end', width: '100%' }}>
                    <Input.Group compact>
                      <Input
                        ref={pageJumpInputRef}
                        placeholder={`Jump (1-${Math.ceil(total / pagination.pageSize)})`}
                        style={{ width: 140 }}
                        type="number"
                        min={1}
                        max={Math.ceil(total / pagination.pageSize)}
                        onPressEnter={(e) => {
                          const pageNumber = parseInt(e.target.value);
                          if (pageNumber) {
                            handlePageJump(pageNumber);
                            e.target.value = '';
                          }
                        }}
                      />
                      <Button
                        onClick={() => {
                          const pageNumber = parseInt(pageJumpInputRef.current?.input?.value);
                          if (pageNumber) {
                            handlePageJump(pageNumber);
                            pageJumpInputRef.current.input.value = '';
                          }
                        }}
                      >
                        Go
                      </Button>
                    </Input.Group>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setGenerateModalVisible(true)}
                      className="seo-action-btn seo-action-btn-primary"
                    >
                      Generate Page
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Card>

            {/* Page Statistics */}
            <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} md={6}>
                <Card className="stat-card">
                  <Statistic
                    title="Total Pages"
                    value={total}
                    prefix={<FileTextOutlined className="icon-file" />}
                    formatter={(value) => value.toLocaleString()}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card className="stat-card">
                  <Statistic
                    title="Enabled Pages"
                    value={
                      pages?.filter((p) => p.status === "active").length || 0
                    }
                    valueStyle={{ color: "#10b981" }}
                    prefix={<CheckCircleOutlined className="icon-check" />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card className="stat-card">
                  <Statistic
                    title="Indexed Pages"
                    value={
                      pages?.filter((p) => p.performanceMetrics?.indexed)
                        .length || 0
                    }
                    valueStyle={{ color: "#667eea" }}
                    prefix={<GlobalOutlined className="icon-global" />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card className="stat-card">
                  <Statistic
                    title="Avg Quality"
                    value={
                      pages && pages.length > 0
                        ? (
                            pages.reduce((sum, p) => sum + p.qualityScore, 0) /
                            pages.length
                          ).toFixed(1)
                        : 0
                    }
                    suffix="/ 100"
                    valueStyle={{ color: "#3f8600" }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Pages Table */}
            <Card className="table-card">
              <Table
                columns={columns}
                dataSource={pages || []}
                rowKey="_id"
                loading={loading}
                pagination={{
                  ...pagination,
                  total,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} pages`,
                }}
                onChange={handleTableChange}
                scroll={{ x: 1200 }}
              />
            </Card>

            {/* Page Details Drawer */}
            <Drawer
              title="Page Details"
              placement="right"
              width={720}
              visible={detailDrawerVisible}
              onClose={() => setDetailDrawerVisible(false)}
            >
              {selectedPage && (
                <Tabs defaultActiveKey="overview">
                  <TabPane tab="Overview" key="overview">
                    <Descriptions bordered column={1}>
                      <Descriptions.Item label="Title">
                        {selectedPage.title}
                      </Descriptions.Item>
                      <Descriptions.Item label="URL">
                        <a
                          href={`${process.env.REACT_APP_LANDING_PAGE_URL}${selectedPage.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {selectedPage.url}
                        </a>
                      </Descriptions.Item>
                      <Descriptions.Item label="Type">
                        <Tag>{selectedPage.pageType}</Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="Status">
                        <Badge
                          status={
                            selectedPage.status === "active"
                              ? "success"
                              : "default"
                          }
                          text={selectedPage.status}
                        />
                      </Descriptions.Item>
                      <Descriptions.Item label="Quality Score">
                        <Progress
                          percent={selectedPage.qualityScore}
                          strokeColor={
                            selectedPage.qualityScore >= 80
                              ? "#52c41a"
                              : selectedPage.qualityScore >= 60
                              ? "#faad14"
                              : "#f5222d"
                          }
                        />
                      </Descriptions.Item>
                      <Descriptions.Item label="Created">
                        {new Date(selectedPage.createdAt).toLocaleString()}
                      </Descriptions.Item>
                      <Descriptions.Item label="Last Updated">
                        {new Date(
                          selectedPage.lastUpdated || selectedPage.updatedAt
                        ).toLocaleString()}
                      </Descriptions.Item>
                    </Descriptions>
                  </TabPane>

                  <TabPane tab="Performance" key="performance">
                    <Descriptions bordered column={1}>
                      <Descriptions.Item label="Indexed">
                        {selectedPage.performanceMetrics?.indexed
                          ? "Yes"
                          : "No"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Organic Traffic">
                        {selectedPage.performanceMetrics?.organicTraffic?.toLocaleString() ||
                          0}
                      </Descriptions.Item>
                      <Descriptions.Item label="Avg Time on Page">
                        {selectedPage.performanceMetrics?.avgTimeOnPage || 0}{" "}
                        seconds
                      </Descriptions.Item>
                      <Descriptions.Item label="Bounce Rate">
                        {selectedPage.performanceMetrics?.bounceRate || 0}%
                      </Descriptions.Item>
                      <Descriptions.Item label="Conversions">
                        {selectedPage.performanceMetrics?.conversions || 0}
                      </Descriptions.Item>
                    </Descriptions>
                  </TabPane>

                  <TabPane tab="SEO Data" key="seo">
                    <Descriptions bordered column={1}>
                      <Descriptions.Item label="Meta Description">
                        {selectedPage.metaDescription}
                      </Descriptions.Item>
                      <Descriptions.Item label="H1 Heading">
                        {selectedPage.h1}
                      </Descriptions.Item>
                      <Descriptions.Item label="Keywords">
                        <Space wrap>
                          {selectedPage.seoData?.keywords?.map((keyword) => (
                            <Tag key={keyword}>{keyword}</Tag>
                          ))}
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label="Canonical URL">
                        {selectedPage.seoData?.canonicalUrl}
                      </Descriptions.Item>
                    </Descriptions>
                  </TabPane>

                  <TabPane tab="Content" key="content">
                    <div style={{ maxHeight: "600px", overflow: "auto" }}>
                      {selectedPage.content && (
                        <pre
                          style={{
                            backgroundColor: "#f5f5f5",
                            padding: "16px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontFamily: "monospace",
                          }}
                        >
                          {JSON.stringify(selectedPage.content, null, 2)}
                        </pre>
                      )}
                    </div>
                  </TabPane>

                  <TabPane tab="Metadata" key="metadata">
                    <div style={{ maxHeight: "600px", overflow: "auto" }}>
                      {selectedPage.metadata && (
                        <pre
                          style={{
                            backgroundColor: "#f5f5f5",
                            padding: "16px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontFamily: "monospace",
                          }}
                        >
                          {JSON.stringify(selectedPage.metadata, null, 2)}
                        </pre>
                      )}
                    </div>
                  </TabPane>
                </Tabs>
              )}
            </Drawer>

            {/* Generate Page Modal */}
            <Modal
              title="Generate Single Page"
              visible={generateModalVisible}
              onCancel={() => setGenerateModalVisible(false)}
              footer={null}
              width={600}
            >
              <Form
                form={generateForm}
                layout="vertical"
                onFinish={handleGeneratePage}
              >
                <Form.Item
                  name="pageType"
                  label="Page Type"
                  rules={[
                    { required: true, message: "Please select page type" },
                  ]}
                >
                  <Select placeholder="Select page type">
                    <Option value="copilotType">AI Copilot</Option>
                    <Option value="industry">Industry Solutions</Option>
                    <Option value="professionalTask">Professional Tasks</Option>
                    <Option value="location">Location Services</Option>
                    <Option value="locationCopilot">Location + Copilot</Option>
                    <Option value="industryCopilot">Industry + Copilot</Option>
                    <Option value="taskCopilot">Task + Copilot</Option>
                    <Option value="comparison">Comparison</Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.pageType !== currentValues.pageType
                  }
                >
                  {({ getFieldValue }) => {
                    const pageType = getFieldValue("pageType");

                    if (pageType === "comparison") {
                      return (
                        <>
                          <Form.Item
                            name={["metadata", "competitor"]}
                            label="Competitor"
                            rules={[
                              {
                                required: true,
                                message: "Please enter competitor details",
                              },
                            ]}
                          >
                            <Input.TextArea
                              placeholder='{"name": "Zapier", "slug": "zapier", "pricing": {"starter": "$19.99/month"}}'
                              rows={3}
                            />
                          </Form.Item>
                          <Form.Item
                            name={["metadata", "useCase"]}
                            label="Use Case"
                            rules={[
                              {
                                required: true,
                                message: "Please enter use case details",
                              },
                            ]}
                          >
                            <Input.TextArea
                              placeholder='{"name": "E-commerce Automation", "slug": "ecommerce-automation", "industry": "e-commerce"}'
                              rows={3}
                            />
                          </Form.Item>
                        </>
                      );
                    }

                    if (pageType === "integration") {
                      return (
                        <>
                          <Form.Item
                            name={["metadata", "integration", "tool1"]}
                            label="Tool 1"
                            rules={[
                              {
                                required: true,
                                message: "Please enter first tool details",
                              },
                            ]}
                          >
                            <Input.TextArea
                              placeholder='{"name": "Slack", "slug": "slack", "category": "communication"}'
                              rows={3}
                            />
                          </Form.Item>
                          <Form.Item
                            name={["metadata", "integration", "tool2"]}
                            label="Tool 2"
                            rules={[
                              {
                                required: true,
                                message: "Please enter second tool details",
                              },
                            ]}
                          >
                            <Input.TextArea
                              placeholder='{"name": "Gmail", "slug": "gmail", "category": "email"}'
                              rows={3}
                            />
                          </Form.Item>
                        </>
                      );
                    }

                    if (pageType === "location") {
                      return (
                        <Form.Item
                          name={["metadata", "location"]}
                          label="Location"
                          rules={[
                            {
                              required: true,
                              message: "Please enter location details",
                            },
                          ]}
                        >
                          <Input.TextArea
                            placeholder='{"cityName": "New York", "state": "NY", "country": "United States", "population": 8336817, "businessCount": 200000}'
                            rows={4}
                          />
                        </Form.Item>
                      );
                    }

                    if (pageType === "useCase") {
                      return (
                        <Form.Item
                          name={["metadata", "useCase"]}
                          label="Use Case"
                          rules={[
                            {
                              required: true,
                              message: "Please enter use case details",
                            },
                          ]}
                        >
                          <Input.TextArea
                            placeholder='{"name": "Lead Generation", "slug": "lead-generation", "industry": "marketing", "description": "Automate lead capture and nurturing"}'
                            rows={4}
                          />
                        </Form.Item>
                      );
                    }

                    if (pageType === "locationUseCase") {
                      return (
                        <>
                          <Form.Item
                            name={["metadata", "location"]}
                            label="Location"
                            rules={[
                              {
                                required: true,
                                message: "Please enter location details",
                              },
                            ]}
                          >
                            <Input.TextArea
                              placeholder='{"cityName": "New York", "state": "NY", "country": "United States", "population": 8336817, "businessCount": 200000}'
                              rows={4}
                            />
                          </Form.Item>
                          <Form.Item
                            name={["metadata", "useCase"]}
                            label="Use Case"
                            rules={[
                              {
                                required: true,
                                message: "Please enter use case details",
                              },
                            ]}
                          >
                            <Input.TextArea
                              placeholder='{"name": "Lead Generation", "slug": "lead-generation", "industry": "marketing", "description": "Automate lead capture and nurturing"}'
                              rows={4}
                            />
                          </Form.Item>
                        </>
                      );
                    }

                    if (pageType === "integrationUseCase") {
                      return (
                        <>
                          <Form.Item
                            name={["metadata", "integration"]}
                            label="Integration"
                            rules={[
                              {
                                required: true,
                                message: "Please enter integration details",
                              },
                            ]}
                          >
                            <Input.TextArea
                              placeholder='{"name": "Slack", "slug": "slack", "category": "communication", "description": "Team communication platform"}'
                              rows={4}
                            />
                          </Form.Item>
                          <Form.Item
                            name={["metadata", "useCase"]}
                            label="Use Case"
                            rules={[
                              {
                                required: true,
                                message: "Please enter use case details",
                              },
                            ]}
                          >
                            <Input.TextArea
                              placeholder='{"name": "Lead Generation", "slug": "lead-generation", "industry": "marketing", "description": "Automate lead capture and nurturing"}'
                              rows={4}
                            />
                          </Form.Item>
                        </>
                      );
                    }

                    return null;
                  }}
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Space>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      Generate Page
                    </Button>
                    <Button onClick={() => setGenerateModalVisible(false)}>
                      Cancel
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Modal>

            {/* Edit Page Modal */}
            <Modal
              title="Edit Page"
              visible={editModalVisible}
              onCancel={() => {
                setEditModalVisible(false);
                editForm.resetFields();
              }}
              footer={null}
              width={800}
            >
              <Form form={editForm} layout="vertical" onFinish={handleUpdate}>
                <Form.Item
                  name="title"
                  label="Title"
                  rules={[
                    { required: true, message: "Please enter page title" },
                  ]}
                >
                  <Input placeholder="Page title for browser tab" />
                </Form.Item>

                <Form.Item
                  name="h1"
                  label="H1 Heading"
                  rules={[
                    { required: true, message: "Please enter H1 heading" },
                  ]}
                >
                  <Input placeholder="Main heading displayed on the page" />
                </Form.Item>

                <Form.Item
                  name="metaDescription"
                  label="Meta Description"
                  rules={[
                    {
                      required: true,
                      message: "Please enter meta description",
                    },
                    {
                      max: 160,
                      message:
                        "Meta description should be under 160 characters",
                    },
                  ]}
                >
                  <Input.TextArea
                    rows={2}
                    placeholder="SEO description (max 160 characters)"
                    showCount
                    maxLength={160}
                  />
                </Form.Item>

                <Form.Item
                  name="content"
                  label="Content (JSON)"
                  rules={[
                    { required: true, message: "Please enter page content" },
                    {
                      validator: (_, value) => {
                        try {
                          if (value) JSON.parse(value);
                          return Promise.resolve();
                        } catch (error) {
                          return Promise.reject(
                            new Error("Invalid JSON format")
                          );
                        }
                      },
                    },
                  ]}
                >
                  <Input.TextArea
                    rows={15}
                    placeholder="Page content in JSON format"
                    style={{ fontFamily: "monospace" }}
                  />
                </Form.Item>

                <Form.Item
                  name="status"
                  label="Status"
                  rules={[{ required: true, message: "Please select status" }]}
                >
                  <Select placeholder="Select page status">
                    <Option value="active">Enabled</Option>
                    <Option value="disabled">Disabled</Option>
                    <Option value="draft">Draft</Option>
                  </Select>
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Space>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      icon={<SaveOutlined />}
                    >
                      Save Changes
                    </Button>
                    <Button
                      onClick={() => {
                        setEditModalVisible(false);
                        editForm.resetFields();
                      }}
                    >
                      Cancel
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Modal>
        </Spin>
      </div>
    </div>
  );
};

export default ProgrammaticSeoPages;
